/**
 * NoteDurationGame - Sheet Music Style Rhythm Game
 * 
 * Displays actual sheet music with notes on a staff.
 * Notes scroll from right to left.
 * Press and HOLD spacebar when note reaches the playhead.
 * Continuous metronome beat in background.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Tone from 'tone';

type NoteType = 'whole' | 'half' | 'quarter' | 'eighth' | 'sixteenth';
type RestType = 'wholeRest' | 'halfRest' | 'quarterRest' | 'eighthRest' | 'sixteenthRest';
type SymbolType = NoteType | RestType;

interface SymbolConfig {
  type: SymbolType;
  name: string;
  beats: number;
  color: string;
  isRest: boolean;
  pitch: number;
}

const SYMBOLS: Record<SymbolType, SymbolConfig> = {
  whole: { type: 'whole', name: 'Whole Note', beats: 4, color: '#f5a623', isRest: false, pitch: 261.63 },
  half: { type: 'half', name: 'Half Note', beats: 2, color: '#81b29a', isRest: false, pitch: 293.66 },
  quarter: { type: 'quarter', name: 'Quarter Note', beats: 1, color: '#e07a5f', isRest: false, pitch: 329.63 },
  eighth: { type: 'eighth', name: 'Eighth Note', beats: 0.5, color: '#e8b86d', isRest: false, pitch: 349.23 },
  sixteenth: { type: 'sixteenth', name: '16th Note', beats: 0.25, color: '#d4a574', isRest: false, pitch: 392.00 },
  wholeRest: { type: 'wholeRest', name: 'Whole Rest', beats: 4, color: '#5a8a9c', isRest: true, pitch: 0 },
  halfRest: { type: 'halfRest', name: 'Half Rest', beats: 2, color: '#6b9a8c', isRest: true, pitch: 0 },
  quarterRest: { type: 'quarterRest', name: 'Quarter Rest', beats: 1, color: '#9a7a6c', isRest: true, pitch: 0 },
  eighthRest: { type: 'eighthRest', name: 'Eighth Rest', beats: 0.5, color: '#8a8a7c', isRest: true, pitch: 0 },
  sixteenthRest: { type: 'sixteenthRest', name: '16th Rest', beats: 0.25, color: '#7a7a6c', isRest: true, pitch: 0 },
};

interface GameSymbol {
  id: number;
  symbol: SymbolType;
  startTime: number;
  config: SymbolConfig;
  hit: boolean;
  missed: boolean;
  rating?: 'PERFECT' | 'GOOD' | 'OK' | 'MISS';
  staffPosition: number; // -6 to 6, where 0 is middle line
}

type GameState = 'menu' | 'countdown' | 'playing' | 'finished';

// Predefined sequences
const SEQUENCES: SymbolType[][] = [
  ['quarter', 'quarter', 'quarter', 'quarter'],
  ['half', 'quarter', 'quarter'],
  ['quarter', 'quarterRest', 'quarter', 'quarter'],
  ['quarter', 'eighth', 'eighth', 'quarter', 'quarter'],
  ['quarter', 'eighth', 'eighth', 'quarterRest', 'eighth', 'eighth', 'quarter'],
  ['quarter', 'sixteenth', 'sixteenth', 'eighth', 'quarter', 'quarter'],
  ['whole'],
  ['half', 'half'],
];

const SCROLL_SPEED = 180; // pixels per second

// Map staff positions to note names (expanded range for better piano coverage)
// Map staff positions to available note names (Salamander piano only has C and A)
const STAFF_NOTE_NAMES: Record<number, string> = {
  6: 'C5',   // top space above staff
  5: 'A4',   // top line
  4: 'C4',   // top space (mapped to C)
  3: 'A4',   // 4th line
  2: 'C4',   // 3rd space (top)
  1: 'A3',   // 3rd line
  0: 'C4',   // 2nd space (middle)
  [-1]: 'A3',  // 2nd line
  [-2]: 'C4',  // 1st space
  [-3]: 'A3',  // 1st line
  [-4]: 'C3',  // bottom space
  [-5]: 'A3',  // first ledger line below
  [-6]: 'C3',  // space below that
};

// Note descriptions for the legend
const NOTE_DESCRIPTIONS = [
  { type: 'whole', name: 'Whole Note', duration: '4 beats', symbol: '○', desc: 'Hold for 4 counts' },
  { type: 'half', name: 'Half Note', duration: '2 beats', symbol: '𝅗𝅥', desc: 'Hold for 2 counts' },
  { type: 'quarter', name: 'Quarter Note', duration: '1 beat', symbol: '♩', desc: 'Hold for 1 count' },
  { type: 'eighth', name: 'Eighth Note', duration: '½ beat', symbol: '♪', desc: 'Quick note' },
  { type: 'sixteenth', name: '16th Note', duration: '¼ beat', symbol: '𝅘𝅥𝅯', desc: 'Very quick note' },
] as const;

export const NoteDurationGame: React.FC = () => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const metronomeSynthRef = useRef<Tone.MembraneSynth | null>(null);
  const pianoSourcesRef = useRef<Map<string, AudioBuffer>>(new Map());
  const activeSourceRef = useRef<Tone.Player | null>(null);
  const reverbRef = useRef<Tone.Reverb | null>(null);
  const animationRef = useRef<number>(0);
  const symbolsRef = useRef<GameSymbol[]>([]);
  const startTimeRef = useRef<number>(0);
  const holdStartTimeRef = useRef<number>(0);
  const activeSymbolRef = useRef<GameSymbol | null>(null);
  const metronomeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const gameEndedRef = useRef(false);
  const audioInitializedRef = useRef(false);
  const samplesLoadedRef = useRef(false);
  
  const [gameState, setGameState] = useState<GameState>('menu');
  const [sequenceIndex, setSequenceIndex] = useState<number>(0);
  const [bpm, setBpm] = useState<number>(60);
  const [countdown, setCountdown] = useState<number>(4);
  const [isHolding, setIsHolding] = useState(false);
  const [score, setScore] = useState({ perfect: 0, good: 0, ok: 0, miss: 0 });
  const [combo, setCombo] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [lastRating, setLastRating] = useState<string | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [audioLoading, setAudioLoading] = useState(false);
  
  const currentSequence = SEQUENCES[sequenceIndex];
  const beatDuration = 60 / bpm;

  const initAudio = useCallback(async () => {
    if (audioInitializedRef.current && samplesLoadedRef.current) return;
    if (audioLoading) return;
    
    setAudioLoading(true);
    
    try {
      // Ensure Tone.js context is started and resume if suspended
      await Tone.start();
      if (Tone.context.state === 'suspended') {
        await Tone.context.resume();
      }
      
      // Create metronome synth once (reused)
      if (!metronomeSynthRef.current) {
        metronomeSynthRef.current = new Tone.MembraneSynth({
          pitchDecay: 0.01,
          octaves: 2,
          oscillator: { type: 'sine' },
          envelope: {
            attack: 0.001,
            decay: 0.1,
            sustain: 0,
            release: 0.01
          }
        }).toDestination();
        metronomeSynthRef.current.volume.value = -10;
      }
      
      // Load piano samples using Web Audio API directly
      if (pianoSourcesRef.current.size === 0) {
        try {
          // Load only available samples (Salamander piano has C and A notes)
        const availableNotes = ['C3', 'A3', 'C4', 'A4', 'C5'];
        for (const note of availableNotes) {
          try {
            const response = await fetch(`/audio/piano/${note}.mp3`);
            if (!response.ok) throw new Error(`Failed to load ${note}`);
            const arrayBuffer = await response.arrayBuffer();
            // Use native Web Audio API for decoding (more reliable)
            const tempContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const audioBuffer = await tempContext.decodeAudioData(arrayBuffer);
            pianoSourcesRef.current.set(note, audioBuffer);
            await tempContext.close();
            console.log(`Loaded ${note}`);
          } catch (e) {
            console.warn(`Failed to load ${note}:`, e);
          }
        }
          
          samplesLoadedRef.current = true;
          console.log('Piano samples loaded via Web Audio API');
          
          // Create reverb effect
          reverbRef.current = new Tone.Reverb({
            decay: 5,
            preDelay: 0.02,
            wet: 0.35
          }).toDestination();
          
        } catch (err) {
          console.warn('Failed to load piano samples:', err);
          samplesLoadedRef.current = false;
        }
      }
      
      audioInitializedRef.current = true;
    } catch (err) {
      console.error('Audio init error:', err);
    } finally {
      setAudioLoading(false);
    }
  }, [audioLoading]);

  const playClick = useCallback((isAccent = false) => {
    if (!audioEnabled || !metronomeSynthRef.current) return;
    
    // Resume audio context if suspended
    if (Tone.context.state === 'suspended') {
      Tone.context.resume();
    }
    
    // Use the reusable metronome synth
    metronomeSynthRef.current.volume.value = isAccent ? -5 : -12;
    metronomeSynthRef.current.triggerAttackRelease(isAccent ? 'C5' : 'G4', '32n');
  }, [audioEnabled]);

  const startNoteTone = useCallback((noteName: string) => {
    if (!audioEnabled || !samplesLoadedRef.current) return;
    
    // Resume audio context if suspended (browser policy)
    if (Tone.context.state === 'suspended') {
      Tone.context.resume();
    }
    
    stopNoteTone();
    
    try {
      const buffer = pianoSourcesRef.current.get(noteName);
      if (!buffer) return;
      
      // Create a Tone.Player for this note
      const player = new Tone.Player(buffer).toDestination();
      
      // Connect through reverb if available
      if (reverbRef.current) {
        player.connect(reverbRef.current);
      }
      
      player.volume.value = -5;
      player.start();
      
      // Store reference to stop later
      activeSourceRef.current = player;
    } catch (err) {
      console.error('Error playing note:', err);
    }
  }, [audioEnabled]);

  const stopNoteTone = useCallback(() => {
    try {
      if (activeSourceRef.current) {
        activeSourceRef.current.stop();
        activeSourceRef.current = null;
      }
    } catch (err) {
      console.error('Error stopping note:', err);
    }
  }, []);

  const stopMetronome = useCallback(() => {
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
      metronomeIntervalRef.current = null;
    }
    setCurrentBeat(0);
  }, []);

  const startMetronome = useCallback(() => {
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
    }
    
    playClick(true);
    setCurrentBeat(1);
    
    let beatCount = 1;
    metronomeIntervalRef.current = setInterval(() => {
      beatCount++;
      const isAccent = beatCount % 4 === 1;
      playClick(isAccent);
      setCurrentBeat(beatCount);
    }, beatDuration * 1000);
  }, [beatDuration, playClick]);

  const buildSymbols = useCallback((): GameSymbol[] => {
    const symbols: GameSymbol[] = [];
    let currentTime = 4 * beatDuration;
    
    currentSequence.forEach((sym, index) => {
      const config = SYMBOLS[sym];
      // Assign staff positions to create melodic patterns
      // Alternating between different lines/spaces
      const staffPositions = [2, 0, 4, 1, 3, -1, 5]; // B, E, F, G, D, C, high G
      const staffPosition = staffPositions[index % staffPositions.length];
      
      symbols.push({
        id: index,
        symbol: sym,
        startTime: currentTime,
        config,
        hit: false,
        missed: false,
        staffPosition,
      });
      currentTime += config.beats * beatDuration;
    });
    
    return symbols;
  }, [currentSequence, beatDuration]);

  const endGame = useCallback(() => {
    if (gameEndedRef.current) return;
    gameEndedRef.current = true;
    // Stop all audio
    stopMetronome();
    stopNoteTone();
    setGameState('finished');
    setCurrentBeat(0);
    setIsHolding(false);
    activeSymbolRef.current = null;
  }, [stopMetronome, stopNoteTone]);

  const startGame = useCallback(async () => {
    await initAudio();
    
    // Reset all game state
    gameEndedRef.current = false;
    setGameState('countdown');
    setCountdown(4);
    setScore({ perfect: 0, good: 0, ok: 0, miss: 0 });
    setCombo(0);
    setTotalScore(0);
    setLastRating(null);
    setCurrentBeat(0);
    setIsHolding(false);
    activeSymbolRef.current = null;
    holdStartTimeRef.current = 0;
    
    // Build fresh symbols
    symbolsRef.current = buildSymbols();
    
    let count = 0;
    playClick(true);
    count++;
    
    const countInterval = setInterval(() => {
      playClick(count === 0);
      setCountdown(4 - count);
      count++;
      if (count >= 4) {
        clearInterval(countInterval);
        setGameState('playing');
        startTimeRef.current = performance.now();
        startMetronome();
      }
    }, beatDuration * 1000);
  }, [beatDuration, buildSymbols, initAudio, playClick, startMetronome]);

  // Handle keyboard input
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat && gameState === 'playing') {
        e.preventDefault();
        if (!isHolding && !gameEndedRef.current) {
          const currentTime = (performance.now() - startTimeRef.current) / 1000;
          const targetSymbol = symbolsRef.current.find(s => 
            !s.hit && !s.missed && 
            Math.abs(s.startTime - currentTime) < 0.3
          );
          
          if (targetSymbol && !targetSymbol.config.isRest) {
            setIsHolding(true);
            holdStartTimeRef.current = performance.now();
            activeSymbolRef.current = targetSymbol;
            // Get the note name based on staff position
            const noteName = STAFF_NOTE_NAMES[targetSymbol.staffPosition] || 'C4';
            startNoteTone(noteName);
          }
        }
      }
    };
    
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && gameState === 'playing') {
        e.preventDefault();
        if (isHolding && activeSymbolRef.current && !gameEndedRef.current) {
          stopNoteTone();
          
          const holdTime = (performance.now() - holdStartTimeRef.current) / 1000;
          const targetSymbol = activeSymbolRef.current;
          const targetDuration = targetSymbol.config.beats * beatDuration;
          
          const durationAccuracy = Math.max(0, 100 - Math.abs(holdTime - targetDuration) / targetDuration * 100);
          
          let rating: 'PERFECT' | 'GOOD' | 'OK' | 'MISS';
          let points = 0;
          
          if (durationAccuracy >= 95) {
            rating = 'PERFECT';
            points = 100 + combo * 10;
            setCombo(c => c + 1);
          } else if (durationAccuracy >= 85) {
            rating = 'GOOD';
            points = 50 + combo * 5;
            setCombo(c => c + 1);
          } else if (durationAccuracy >= 70) {
            rating = 'OK';
            points = 25;
            setCombo(0);
          } else {
            rating = 'MISS';
            points = 0;
            setCombo(0);
          }
          
          targetSymbol.hit = true;
          targetSymbol.rating = rating;
          
          setScore(s => ({ ...s, [rating.toLowerCase()]: s[rating.toLowerCase() as keyof typeof s] + 1 }));
          setTotalScore(s => s + points);
          setLastRating(rating);
          setTimeout(() => setLastRating(null), 500);
          
          setIsHolding(false);
          activeSymbolRef.current = null;
          
          // Check if all notes are done
          const allDone = symbolsRef.current.every(s => s.hit || s.missed);
          if (allDone) {
            setTimeout(endGame, 500);
          }
        }
      }
    };
    
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [gameState, isHolding, beatDuration, combo, startNoteTone, stopNoteTone, endGame]);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const render = () => {
      const W = canvas.width;
      const H = canvas.height;
      
      // Clear
      ctx.fillStyle = '#0a0a0b';
      ctx.fillRect(0, 0, W, H);
      
      // Staff settings
      const staffY = H / 2;
      const lineSpacing = 14;
      const staffHeight = 4 * lineSpacing;
      const staffTop = staffY - staffHeight / 2;
      const staffBottom = staffY + staffHeight / 2;
      const playheadX = W * 0.3;
      
      // Draw staff background
      ctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
      ctx.fillRect(0, staffTop - 20, W, staffHeight + 40);
      
      // Draw 5 staff lines
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 1.5;
      for (let i = 0; i < 5; i++) {
        const y = staffTop + i * lineSpacing;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }
      
      // Draw G clef at the beginning
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '48px serif';
      ctx.textAlign = 'left';
      ctx.fillText('𝄞', 10, staffY + 8);
      
      // Draw playhead (vertical line)
      ctx.strokeStyle = '#f5a623';
      ctx.lineWidth = 3;
      ctx.shadowColor = '#f5a623';
      ctx.shadowBlur = 15;
      ctx.beginPath();
      ctx.moveTo(playheadX, staffTop - 30);
      ctx.lineTo(playheadX, staffBottom + 30);
      ctx.stroke();
      ctx.shadowBlur = 0;
      
      // Playhead triangle
      ctx.fillStyle = '#f5a623';
      ctx.beginPath();
      ctx.moveTo(playheadX, staffTop - 40);
      ctx.lineTo(playheadX - 10, staffTop - 55);
      ctx.lineTo(playheadX + 10, staffTop - 55);
      ctx.fill();
      
      // Draw beat indicator
      if ((gameState === 'playing' || gameState === 'countdown') && currentBeat > 0) {
        const beatInMeasure = ((currentBeat - 1) % 4) + 1;
        const indicatorY = 25;
        
        for (let i = 1; i <= 4; i++) {
          const bx = W / 2 + (i - 2.5) * 35;
          const isCurrent = i === beatInMeasure;
          
          ctx.beginPath();
          ctx.arc(bx, indicatorY, isCurrent ? 10 : 6, 0, Math.PI * 2);
          ctx.fillStyle = isCurrent ? '#f5a623' : i === 1 ? 'rgba(245, 166, 35, 0.4)' : 'rgba(255, 255, 255, 0.15)';
          ctx.fill();
          
          if (isCurrent) {
            ctx.shadowColor = '#f5a623';
            ctx.shadowBlur = 10;
            ctx.stroke();
            ctx.shadowBlur = 0;
          }
          
          ctx.font = isCurrent ? 'bold 11px "Space Grotesk"' : '10px "Space Grotesk"';
          ctx.textAlign = 'center';
          ctx.fillStyle = isCurrent ? '#0a0a0b' : 'rgba(255, 255, 255, 0.4)';
          ctx.fillText(String(i), bx, indicatorY + (isCurrent ? 3 : 2));
        }
      }
      
      // Draw game symbols
      if (gameState === 'playing' || gameState === 'countdown') {
        const currentTime = gameState === 'playing' 
          ? (performance.now() - startTimeRef.current) / 1000
          : 0;
        
        symbolsRef.current.forEach(symbol => {
          const timeUntilHit = symbol.startTime - currentTime;
          const x = playheadX + timeUntilHit * SCROLL_SPEED;
          
          if (x < -100 || x > W + 100) return;
          
          const config = symbol.config;
          const isRest = config.isRest;
          
          // Calculate Y position based on staff position
          // staffPosition: -6 to 6, where 0 is middle line (B)
          const noteY = staffY - symbol.staffPosition * (lineSpacing / 2);
          
          // Draw ledger lines if note is outside main staff
          if (symbol.staffPosition > 4 || symbol.staffPosition < -4) {
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
            ctx.lineWidth = 1.5;
            const ledgerY = symbol.staffPosition > 4 
              ? staffTop - (symbol.staffPosition - 4) * (lineSpacing / 2)
              : staffBottom + (-4 - symbol.staffPosition) * (lineSpacing / 2);
            ctx.beginPath();
            ctx.moveTo(x - 20, ledgerY);
            ctx.lineTo(x + 20, ledgerY);
            ctx.stroke();
          }
          
          // Draw duration bar below staff
          const barY = staffBottom + 35;
          const barWidth = config.beats * SCROLL_SPEED * beatDuration - 20;
          
          // Background
          ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
          ctx.fillRect(x - 10, barY, Math.max(barWidth, 20), 10);
          
          // Target
          ctx.fillStyle = config.color + '40';
          ctx.fillRect(x - 10, barY, Math.max(barWidth, 20), 10);
          
          // Progress if holding
          if (activeSymbolRef.current?.id === symbol.id && isHolding) {
            const holdTime = (performance.now() - holdStartTimeRef.current) / 1000;
            const progress = Math.min(holdTime / (config.beats * beatDuration), 1);
            ctx.fillStyle = config.color;
            ctx.fillRect(x - 10, barY, Math.max(barWidth, 20) * progress, 10);
          }
          
          // Result if hit
          if (symbol.hit && symbol.rating) {
            const colors = { PERFECT: '#81b29a', GOOD: '#e8b86d', OK: '#e07a5f', MISS: '#a8a29e' };
            ctx.fillStyle = colors[symbol.rating];
            ctx.fillRect(x - 10, barY, Math.max(barWidth, 20), 10);
          }
          
          // Draw note head
          ctx.fillStyle = symbol.hit ? config.color + '40' : symbol.missed ? '#a8a29e60' : config.color;
          ctx.strokeStyle = symbol.hit ? config.color + '40' : symbol.missed ? '#a8a29e' : config.color;
          
          if (isRest) {
            ctx.fillStyle = symbol.hit ? '#5a8a9c40' : '#5a8a9c';
            switch (config.type) {
              case 'wholeRest':
                ctx.fillRect(x - 12, staffTop + lineSpacing - 3, 24, 6);
                break;
              case 'halfRest':
                ctx.fillRect(x - 12, staffTop + 2 * lineSpacing - 3, 24, 6);
                break;
              case 'quarterRest':
                ctx.font = '32px serif';
                ctx.fillText('𝄽', x - 10, noteY + 8);
                break;
              case 'eighthRest':
                ctx.font = '28px serif';
                ctx.fillText('𝄾', x - 8, noteY + 6);
                break;
              case 'sixteenthRest':
                ctx.font = '24px serif';
                ctx.fillText('𝄿', x - 6, noteY + 4);
                break;
            }
          } else {
            // Draw note head
            ctx.beginPath();
            ctx.ellipse(x, noteY, 10, 8, 0, 0, Math.PI * 2);
            if (config.type === 'whole' || config.type === 'half') {
              ctx.stroke();
              ctx.fillStyle = symbol.hit || symbol.missed ? ctx.fillStyle : '#0a0a0b';
              ctx.fill();
            } else {
              ctx.fill();
            }
            
            // Draw stem
            const stemDirection = symbol.staffPosition >= 2 ? -1 : 1;
            const stemLength = 35;
            ctx.beginPath();
            ctx.moveTo(x + (stemDirection === -1 ? 8 : -8), noteY);
            ctx.lineTo(x + (stemDirection === -1 ? 8 : -8), noteY + stemDirection * stemLength);
            ctx.stroke();
            
            // Draw flags for eighth and sixteenth
            if (config.type === 'eighth' || config.type === 'sixteenth') {
              const flagX = x + (stemDirection === -1 ? 8 : -8);
              const flagY = noteY + stemDirection * stemLength;
              ctx.beginPath();
              if (stemDirection === -1) {
                ctx.moveTo(flagX, flagY);
                ctx.quadraticCurveTo(flagX + 15, flagY + 8, flagX, flagY + 18);
              } else {
                ctx.moveTo(flagX, flagY);
                ctx.quadraticCurveTo(flagX - 15, flagY - 8, flagX, flagY - 18);
              }
              ctx.stroke();
              
              if (config.type === 'sixteenth') {
                ctx.beginPath();
                if (stemDirection === -1) {
                  ctx.moveTo(flagX, flagY + 10);
                  ctx.quadraticCurveTo(flagX + 15, flagY + 18, flagX, flagY + 28);
                } else {
                  ctx.moveTo(flagX, flagY - 10);
                  ctx.quadraticCurveTo(flagX - 15, flagY - 18, flagX, flagY - 28);
                }
                ctx.stroke();
              }
            }
          }
          
          // Rating text
          if (symbol.hit && symbol.rating) {
            ctx.font = 'bold 12px "Space Grotesk"';
            ctx.textAlign = 'center';
            const colors = { PERFECT: '#81b29a', GOOD: '#e8b86d', OK: '#e07a5f', MISS: '#a8a29e' };
            ctx.fillStyle = colors[symbol.rating];
            ctx.fillText(symbol.rating[0], x, staffTop - 25);
          }
          
          // Miss check - only mark as missed if not currently being held
          if (!symbol.hit && !symbol.missed && timeUntilHit < -0.3 && !isRest && !gameEndedRef.current 
              && activeSymbolRef.current?.id !== symbol.id) {
            symbol.missed = true;
            setCombo(0);
            setScore(s => ({ ...s, miss: s.miss + 1 }));
            
            // Check end game
            if (symbolsRef.current.every(s => s.hit || s.missed)) {
              setTimeout(endGame, 500);
            }
          }
        });
      }
      
      // Last rating
      if (lastRating) {
        ctx.font = 'bold 28px "Space Grotesk"';
        ctx.textAlign = 'center';
        const colors = { PERFECT: '#81b29a', GOOD: '#e8b86d', OK: '#e07a5f', MISS: '#a8a29e' };
        ctx.fillStyle = colors[lastRating as keyof typeof colors];
        ctx.fillText(lastRating, playheadX, staffTop - 60);
      }
      
      animationRef.current = requestAnimationFrame(render);
    };
    
    animationRef.current = requestAnimationFrame(render);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [gameState, isHolding, lastRating, beatDuration, currentBeat]);

  // Cleanup
  useEffect(() => {
    return () => {
      stopMetronome();
      stopNoteTone();
      // Dispose synths to free up resources
      if (metronomeSynthRef.current) {
        metronomeSynthRef.current.dispose();
        metronomeSynthRef.current = null;
      }
      if (activeSourceRef.current) {
        activeSourceRef.current.stop();
        activeSourceRef.current = null;
      }
      pianoSourcesRef.current.clear();
      if (reverbRef.current) {
        reverbRef.current.dispose();
        reverbRef.current = null;
      }
      audioInitializedRef.current = false;
      samplesLoadedRef.current = false;
    };
  }, [stopMetronome, stopNoteTone]);

  const totalNotes = currentSequence.filter(s => !SYMBOLS[s].isRest).length;
  const hitNotes = score.perfect + score.good + score.ok;
  const avgAccuracy = hitNotes > 0 
    ? Math.round((score.perfect * 100 + score.good * 85 + score.ok * 70) / hitNotes)
    : 0;

  return (
    <div className="min-h-screen flex flex-col bg-studio-950">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-studio-950 border-b border-studio-800">
        <div className="flex items-center gap-4">
          <h1 className="text-stone-100 font-display text-xl font-bold">Note Duration Practice</h1>
          <span className="text-stone-500 text-sm">Read the rhythm, hold for full value</span>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setAudioEnabled(!audioEnabled)}
            disabled={audioLoading}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              audioLoading
                ? 'bg-studio-800 text-stone-500 border border-studio-700 cursor-wait'
                : audioEnabled 
                  ? 'bg-sage-500/20 text-sage-400 border border-sage-500/50' 
                  : 'bg-studio-800 text-stone-500 border border-studio-700'
            }`}
          >
            {audioLoading ? '⏳ Loading...' : audioEnabled ? '🔊 Audio On' : '🔇 Audio Off'}
          </button>
          
          <select
            value={sequenceIndex}
            onChange={(e) => setSequenceIndex(Number(e.target.value))}
            disabled={gameState === 'playing' || gameState === 'countdown'}
            className="px-3 py-1.5 bg-studio-800 text-stone-200 rounded-lg text-sm border border-studio-700 focus:border-amber-500 outline-none"
          >
            <option value={0}>Easy: 4 Quarters</option>
            <option value={1}>Easy: Half & Quarters</option>
            <option value={2}>Medium: With Rests</option>
            <option value={3}>Medium: Eighth Notes</option>
            <option value={4}>Hard: Complex</option>
            <option value={5}>Hard: Sixteenths</option>
            <option value={6}>Practice: Whole Note</option>
            <option value={7}>Practice: Half Notes</option>
          </select>
          
          <div className="flex items-center gap-1">
            {[60, 80, 100].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setBpm(t)}
                disabled={gameState === 'playing' || gameState === 'countdown'}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                  bpm === t ? 'bg-amber-500 text-studio-950' : 'bg-studio-800 text-stone-400 hover:text-stone-200'
                } ${(gameState === 'playing' || gameState === 'countdown') ? 'opacity-50' : ''}`}
              >
                {t}
              </button>
            ))}
          </div>
          
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-stone-500 hover:text-stone-300 text-sm font-medium"
          >
            Exit
          </button>
        </div>
      </div>

      {/* Score bar */}
      {gameState !== 'menu' && (
        <div className="flex items-center justify-center gap-8 py-2 bg-studio-900 border-b border-studio-800">
          <div className="text-center">
            <span className="text-stone-500 text-xs uppercase">Score</span>
            <p className="text-xl font-bold text-amber-500">{totalScore}</p>
          </div>
          <div className="text-center">
            <span className="text-stone-500 text-xs uppercase">Combo</span>
            <p className="text-xl font-bold text-gold-400">{combo}x</p>
          </div>
          <div className="text-center">
            <span className="text-stone-500 text-xs uppercase">Notes</span>
            <p className="text-xl font-bold text-stone-200">{hitNotes + score.miss}/{totalNotes}</p>
          </div>
        </div>
      )}
       {/* Note Legend */}
       {gameState === 'menu' && (
        <div className="px-6 py-4 bg-studio-900 border-t border-studio-800">
          <p className="text-stone-400 text-xs uppercase tracking-wider mb-3 text-center">Note Reference</p>
          <div className="grid grid-cols-5 gap-4 max-w-4xl mx-auto">
            {NOTE_DESCRIPTIONS.map((note) => (
              <div key={note.type} className="bg-studio-800 rounded-lg p-3 text-center">
                <div className="text-2xl mb-1" style={{ color: SYMBOLS[note.type].color }}>
                  {note.symbol}
                </div>
                <p className="text-stone-200 font-medium text-sm">{note.name}</p>
                <p className="text-amber-500 text-xs font-mono">{note.duration}</p>
                <p className="text-stone-500 text-xs mt-1">{note.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Game area */}
      <div className="flex-1 relative flex flex-col">
        <div className="text-center py-3">
          <p className="text-stone-400 text-sm">
            {gameState === 'menu' && "Press SPACE when notes reach the line, hold for duration"}
            {gameState === 'countdown' && "Get ready..."}
            {gameState === 'playing' && (isHolding ? 
              <span className="text-amber-500 font-bold animate-pulse">HOLDING...</span> : 
              "Press and HOLD SPACE for note duration"
            )}
          </p>
        </div>
        
        <div className="flex-1 relative">
          <canvas ref={canvasRef} width={900} height={350} className="w-full h-full block" />
          
          {gameState === 'menu' && (
            <div className="absolute inset-0 flex items-center justify-center bg-studio-950/60">
              <button
                type="button"
                onClick={startGame}
                className="px-10 py-4 bg-amber-500 text-studio-950 font-bold text-xl rounded-xl hover:bg-amber-400 transition-all shadow-lg shadow-amber-500/30"
              >
                Start Practice
              </button>
            </div>
          )}
          
          {gameState === 'countdown' && countdown > 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="font-display text-[9rem] font-bold text-amber-500 drop-shadow-[0_0_30px_rgba(245,166,35,0.5)]">
                {countdown}
              </div>
            </div>
          )}
          
          {gameState === 'finished' && (
            <div className="absolute inset-0 bg-studio-950/95 flex items-center justify-center">
              <div className="text-center max-w-md p-8 bg-studio-900 rounded-2xl border border-studio-700">
                <h2 className="font-display text-4xl font-bold text-stone-100 mb-6">Complete!</h2>
                
                <div className="text-6xl font-bold mb-2" style={{ 
                  color: avgAccuracy >= 90 ? '#81b29a' : avgAccuracy >= 75 ? '#e8b86d' : '#e07a5f'
                }}>
                  {avgAccuracy}%
                </div>
                <p className="text-stone-400 mb-6">Accuracy</p>
                
                <div className="grid grid-cols-4 gap-3 mb-6">
                  <div className="bg-studio-800 rounded-lg p-3">
                    <p className="text-xl font-bold text-sage-400">{score.perfect}</p>
                    <p className="text-stone-500 text-xs">Perfect</p>
                  </div>
                  <div className="bg-studio-800 rounded-lg p-3">
                    <p className="text-xl font-bold text-gold-400">{score.good}</p>
                    <p className="text-stone-500 text-xs">Good</p>
                  </div>
                  <div className="bg-studio-800 rounded-lg p-3">
                    <p className="text-xl font-bold text-coral-400">{score.ok}</p>
                    <p className="text-stone-500 text-xs">OK</p>
                  </div>
                  <div className="bg-studio-800 rounded-lg p-3">
                    <p className="text-xl font-bold text-stone-500">{score.miss}</p>
                    <p className="text-stone-500 text-xs">Miss</p>
                  </div>
                </div>
                
                <div className="flex gap-4 justify-center">
                  <button onClick={startGame} className="px-6 py-3 bg-amber-500 text-studio-950 font-semibold rounded-xl hover:bg-amber-400">Try Again</button>
                  <button onClick={() => setGameState('menu')} className="px-6 py-3 bg-studio-800 text-stone-300 font-semibold rounded-xl hover:bg-studio-700">Change</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <div className="px-6 py-3 bg-studio-950 border-t border-studio-800">
        <div className="flex items-center justify-center gap-8 text-sm text-stone-400">
          <div className="flex items-center gap-2">
            <kbd className="px-3 py-1.5 bg-studio-800 rounded-lg text-amber-500 font-mono font-bold">SPACE</kbd>
            <span>Hold for note duration</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sage-400">● Perfect</span>
            <span className="text-gold-400">● Good</span>
            <span className="text-coral-400">● OK</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteDurationGame;
